import type { AnimationFrame, AnimationMode } from "./animation";

export type PlaybackState =
  | { readonly mode: AnimationMode; readonly status: "idle" }
  | {
      readonly mode: AnimationMode;
      readonly status: "paused";
      readonly elapsedMs: number;
    }
  | {
      readonly mode: AnimationMode;
      readonly status: "playing";
      readonly elapsedMs: number;
      readonly runningSinceMs: number;
    };

export function togglePlayback(
  playback: PlaybackState,
  now: number,
): PlaybackState {
  if (playback.status === "playing") return pausePlayback(playback, now);
  if (playback.status === "paused") {
    assertClock(now);
    assertElapsed(playback.elapsedMs);
    return {
      mode: playback.mode,
      status: "playing",
      elapsedMs: playback.elapsedMs,
      runningSinceMs: now,
    };
  }
  assertClock(now);
  return {
    mode: playback.mode,
    status: "playing",
    elapsedMs: 0,
    runningSinceMs: now,
  };
}

export function resetPlayback(playback: PlaybackState): PlaybackState {
  return createIdlePlayback(playback.mode);
}

export function createIdlePlayback(mode: AnimationMode): PlaybackState {
  assertAnimationMode(mode);
  return { mode, status: "idle" };
}

export function pausePlayback(
  playback: PlaybackState,
  now: number,
): PlaybackState {
  if (playback.status !== "playing") return playback;
  return {
    mode: playback.mode,
    status: "paused",
    elapsedMs: getPlaybackElapsed(playback, now),
  };
}

export function restorePausedPlayback(
  mode: AnimationMode,
  elapsedMs: number,
): PlaybackState {
  assertAnimationMode(mode, "Invalid paused animation");
  if (!Number.isFinite(elapsedMs) || elapsedMs < 0)
    throw new Error("Invalid paused animation");
  return { mode, status: "paused", elapsedMs };
}

function assertAnimationMode(
  mode: unknown,
  message = "Invalid animation mode",
): asserts mode is AnimationMode {
  if (mode !== "steps" && mode !== "composed") throw new Error(message);
}

export function getPlaybackElapsed(
  playback: PlaybackState,
  now: number,
): number {
  if (playback.status === "idle") return 0;
  assertElapsed(playback.elapsedMs);
  if (playback.status === "paused") return playback.elapsedMs;
  assertClock(now);
  assertClock(playback.runningSinceMs);
  const elapsed = playback.elapsedMs + now - playback.runningSinceMs;
  if (!Number.isFinite(elapsed) || elapsed < playback.elapsedMs)
    throw new Error("Invalid playback clock");
  return elapsed;
}

export function getAnimationFrame(
  playback: PlaybackState,
  now: number,
): AnimationFrame | null {
  return playback.status === "idle"
    ? null
    : { mode: playback.mode, elapsedMs: getPlaybackElapsed(playback, now) };
}

function assertClock(now: number): void {
  if (!Number.isFinite(now) || now < 0)
    throw new Error("Invalid playback clock");
}

function assertElapsed(elapsedMs: number): void {
  if (!Number.isFinite(elapsedMs) || elapsedMs < 0)
    throw new Error("Invalid playback elapsed time");
}
